import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  birthday: z.string().min(1, "Birthday is required"),
  location: z.string().min(1, "Location is required"),
  challenges: z.string().optional(),
  height: z
    .number({ invalid_type_error: "Height must be a number" })
    .positive("Height must be greater than 0"),
  weight: z
    .number({ invalid_type_error: "Weight must be a number" })
    .positive("Weight must be greater than 0"),
  agreedToTerms: z.literal(true, {
    errorMap: () => ({ message: "You must agree to the terms" }),
  }),
});

export default function GetToKnowYouPage() {
  const navigate = useNavigate();
  const { token } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      birthday: "",
      location: "",
      challenges: "",
      height: "",
      weight: "",
      agreedToTerms: false,
    },
  });

  const onSubmit = async (data) => {
    try {
      const response = await fetch("/user/profile", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        // body: JSON.stringify(data),
      });

      if (!response.ok) {
        const err = await response.json();
        console.error("Validation or server error:", err);
        alert(err.error || "Failed to save data");
        return;
      }

      const result = await response.json();
      console.log("Saved successfully:", result);

      navigate("/dashboard");
    } catch (error) {
      console.error("failed:", error);
      alert("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="md:w-3/5 mx-auto px-4 py-10">
      <h1 className="text-3xl text-[#281D1BB2] font-semibold mb-8 text-center">
        Let's Get to Know You
      </h1>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Name */}
        <div className="flex items-center justify-between">
          <label className="w-1/2 font-light">What's your name?</label>
          <div className="w-1/2">
            <input
              {...register("name")}
              className="w-full border px-3 py-2 rounded"
            />
            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name.message}</p>
            )}
          </div>
        </div>

        {/* Birthday */}
        <div className="flex items-center justify-between">
          <label className="w-1/2 font-light">When's your birthday?</label>
          <div className="w-1/2">
            <input
              type="date"
              {...register("birthday")}
              className="w-full border px-3 py-2 rounded"
            />
            {errors.birthday && (
              <p className="text-red-500 text-sm">{errors.birthday.message}</p>
            )}
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center justify-between">
          <label className="w-1/2 font-light">Where are you located?</label>
          <div className="w-1/2">
            <input
              {...register("location")}
              className="w-full border px-3 py-2 rounded"
            />
            {errors.location && (
              <p className="text-red-500 text-sm">{errors.location.message}</p>
            )}
          </div>
        </div>

        {/* Challenges */}
        <div className="flex items-center justify-between">
          <label className="w-1/2 font-light">
            What are your biggest challenges right now?
          </label>
          <div className="w-1/2">
            <input
              {...register("challenges")}
              className="w-full border px-3 py-2 rounded"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between">
          <label className="w-1/2 font-light">
            Tell us about your current stats
          </label>
          <div className="w-1/2 flex gap-2">
            <input
              type="number"
              placeholder="Height (cm)"
              {...register("height", { valueAsNumber: true })}
              className="w-full flex-1 border px-3 py-2 rounded"
            />
            <input
              type="number"
              placeholder="Weight (kg)"
              {...register("weight", { valueAsNumber: true })}
              className="w-full flex-1 border px-3 py-2 rounded"
            />
          </div>
        </div>
        {errors.height && (
          <p className="text-red-500 text-sm">{errors.height.message}</p>
        )}
        {errors.weight && (
          <p className="text-red-500 text-sm">{errors.weight.message}</p>
        )}

        {/* Checkbox */}
        <div className="flex items-center justify-center gap-2">
          <input
            type="checkbox"
            id="agreedToTerms"
            {...register("agreedToTerms")}
            className="h-4 w-4"
          />
          <label htmlFor="agreedToTerms" className="text-sm font-light">
            I agree to the terms and conditions
          </label>
        </div>
        {errors.agreedToTerms && (
          <p className="text-red-500 text-center text-sm">
            {errors.agreedToTerms.message}
          </p>
        )}

        {/* Submit */}
        <button
          type="submit"
          className="rounded-full block w-fit mx-auto px-8 bg-[image:--gradient] text-white py-3"
          style={{
            "--gradient": "linear-gradient(90deg, #CC1B10 0%, #970900 100%)",
            opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          I Agree and Continue
        </button>
      </form>
    </div>
  );
}
